import React, { useEffect, useState } from 'react';
import {
  Plus, Search, Megaphone, Edit3, Trash2, Eye, X, Calendar,
  Tag, Flag, Building2, User, Clock, CheckCircle, AlertCircle,
  DollarSign, Phone, Mail, MessageSquare, Users, Globe, MapPin,
  BarChart3, Briefcase, Target, Handshake
} from 'lucide-react';
import {
  getMarketingActivities, createMarketingActivity, updateMarketingActivity,
  deleteMarketingActivity, getMarketingStats, getMarketingLogs, createMarketingLog,
  getUsers,
  getMarketingClients, createMarketingClient, updateMarketingClient,
  deleteMarketingClient, getMarketingClientStats
} from '../../api';

const JENIS_OPTIONS = [
  { value: 'marketing_support', label: 'Marketing & Sales Support', icon: Megaphone, color: '#7c3aed' },
  { value: 'client_relations', label: 'Client Relations', icon: Handshake, color: '#0891b2' },
  { value: 'admin_support', label: 'Admin Support', icon: Briefcase, color: '#ea580c' },
  { value: 'reporting', label: 'Reporting', icon: BarChart3, color: '#16a34a' },
];

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed', 'Cancelled'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const STATUS_PENAWARAN_OPTIONS = ['Dikirim', 'Diproses', 'Diterima', 'Ditolak'];

const KATEGORI_OPTIONS = ['Prospek', 'Klien Aktif', 'Klien Lama', 'Partner', 'Vendor'];
const SUMBER_OPTIONS = ['Website', 'Referral', 'Cold Call', 'Email', 'Sosial Media', 'Lainnya'];
const CLIENT_STATUS_OPTIONS = ['Active', 'Inactive', 'On Hold'];

const statusStyles = {
  'Pending': { bg: '#fef3c7', color: '#92400e' },
  'In Progress': { bg: '#dbeafe', color: '#1e40af' },
  'Completed': { bg: '#d1fae5', color: '#065f46' },
  'Cancelled': { bg: '#f3f4f6', color: '#6b7280' },
};

const priorityColors = {
  'Low': '#6b7280',
  'Medium': '#f59e0b',
  'High': '#ef4444',
  'Urgent': '#dc2626',
};

const kategoriColors = {
  'Prospek': '#7c3aed',
  'Klien Aktif': '#16a34a',
  'Klien Lama': '#6b7280',
  'Partner': '#0891b2',
  'Vendor': '#ea580c',
};

const clientStatusStyles = {
  'Active': { bg: '#d1fae5', color: '#065f46' },
  'Inactive': { bg: '#f3f4f6', color: '#6b7280' },
  'On Hold': { bg: '#fef3c7', color: '#92400e' },
};

const jenisInfo = Object.fromEntries(JENIS_OPTIONS.map(j => [j.value, j]));

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCurrency(v) {
  if (!v || v === 0) return '-';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(v);
}

function isOverdue(deadline, status) {
  if (!deadline || status === 'Completed' || status === 'Cancelled') return false;
  return new Date(deadline) < new Date();
}

// =============================================
// STAT CARDS
// =============================================
function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: 14,
      border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: color + '14', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{value}</div>
        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{label}</div>
        {sub && <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// =============================================
// ACTIVITY FORM MODAL
// =============================================
function ActivityForm({ onSave, onClose, initial, users }) {
  const [form, setForm] = useState(initial || {
    judul: '', deskripsi: '', jenis: 'marketing_support', status: 'Pending', priority: 'Medium',
    nama_prospek: '', perusahaan: '', kontak_person: '', no_hp: '', email: '',
    nilai_penawaran: 0, tgl_penawaran: '', tgl_jatuh_tempo: '', status_penawaran: '',
    tanggal: new Date().toISOString().slice(0, 10), deadline: '', tgl_followup: '',
    assigned_to: null, bagian: '', progress: 0, catatan: '', log_catatan: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.judul.trim()) { setError('Judul harus diisi'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        nilai_penawaran: parseFloat(form.nilai_penawaran) || 0,
        assigned_to: form.assigned_to ? parseInt(form.assigned_to) : null,
        tgl_penawaran: form.tgl_penawaran || null,
        tgl_jatuh_tempo: form.tgl_jatuh_tempo || null,
        deadline: form.deadline || null,
        tgl_followup: form.tgl_followup || null,
      };
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0',
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Megaphone size={20} style={{ color: '#7c3aed' }} />
            {initial ? 'Edit Marketing Activity' : 'Tambah Marketing Activity'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#6b7280" />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.85rem' }}>
              <AlertCircle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Judul *</label>
              <input style={inputStyle} value={form.judul} onChange={e => set('judul', e.target.value)}
                placeholder="Contoh: Proposal CCTV untuk PT X" />
            </div>
            <div>
              <label style={labelStyle}>Jenis Aktivitas</label>
              <select style={inputStyle} value={form.jenis} onChange={e => set('jenis', e.target.value)}>
                {JENIS_OPTIONS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select style={inputStyle} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Progress ({form.progress}%)</label>
              <input type="range" min="0" max="100" value={form.progress}
                onChange={e => set('progress', parseInt(e.target.value))}
                style={{ width: '100%', marginTop: 8 }} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Deskripsi</label>
            <textarea style={{ ...inputStyle, minHeight: 60 }} value={form.deskripsi}
              onChange={e => set('deskripsi', e.target.value)} placeholder="Deskripsi aktivitas..." rows={2} />
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0', paddingTop: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Building2 size={14} /> Informasi Klien / Prospek
            </h4>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Nama Prospek</label>
              <input style={inputStyle} value={form.nama_prospek} onChange={e => set('nama_prospek', e.target.value)}
                placeholder="Nama calon klien" />
            </div>
            <div>
              <label style={labelStyle}>Perusahaan</label>
              <input style={inputStyle} value={form.perusahaan} onChange={e => set('perusahaan', e.target.value)}
                placeholder="Nama perusahaan" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Kontak Person</label>
              <input style={inputStyle} value={form.kontak_person} onChange={e => set('kontak_person', e.target.value)}
                placeholder="Nama kontak" />
            </div>
            <div>
              <label style={labelStyle}>No HP</label>
              <input style={inputStyle} value={form.no_hp} onChange={e => set('no_hp', e.target.value)}
                placeholder="08xxxxxxxxxx" />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="email@domain.com" />
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0', paddingTop: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <DollarSign size={14} /> Informasi Penawaran
            </h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Nilai Penawaran (Rp)</label>
              <input style={inputStyle} type="number" value={form.nilai_penawaran}
                onChange={e => set('nilai_penawaran', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Tanggal Penawaran</label>
              <input style={inputStyle} type="date" value={form.tgl_penawaran || ''}
                onChange={e => set('tgl_penawaran', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Status Penawaran</label>
              <select style={inputStyle} value={form.status_penawaran} onChange={e => set('status_penawaran', e.target.value)}>
                <option value="">-</option>
                {STATUS_PENAWARAN_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Tgl Jatuh Tempo</label>
              <input style={inputStyle} type="date" value={form.tgl_jatuh_tempo || ''}
                onChange={e => set('tgl_jatuh_tempo', e.target.value)} />
            </div>
            <div />
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0', paddingTop: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={14} /> Jadwal & Penugasan
            </h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Tanggal</label>
              <input style={inputStyle} type="date" value={form.tanggal}
                onChange={e => set('tanggal', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Deadline</label>
              <input style={inputStyle} type="date" value={form.deadline || ''}
                onChange={e => set('deadline', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Tgl Follow-up</label>
              <input style={inputStyle} type="date" value={form.tgl_followup || ''}
                onChange={e => set('tgl_followup', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Ditugaskan Ke</label>
              <select style={inputStyle} value={form.assigned_to || ''}
                onChange={e => set('assigned_to', e.target.value || null)}>
                <option value="">Belum ditugaskan</option>
                {(users || []).map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Bagian</label>
              <input style={inputStyle} value={form.bagian} onChange={e => set('bagian', e.target.value)}
                placeholder="Bagian / departemen" />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Catatan</label>
            <textarea style={{ ...inputStyle, minHeight: 60 }} value={form.catatan}
              onChange={e => set('catatan', e.target.value)} placeholder="Catatan internal..." rows={2} />
          </div>

          {initial && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                <MessageSquare size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                Tambah Log Aktivitas (opsional)
              </label>
              <textarea style={{ ...inputStyle, minHeight: 50 }} value={form.log_catatan || ''}
                onChange={e => set('log_catatan', e.target.value)} placeholder="Catatan perubahan / follow-up..." rows={2} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8,
              background: '#fff', cursor: 'pointer', fontSize: '0.875rem',
            }}>Batal</button>
            <button type="submit" disabled={saving} style={{
              padding: '10px 24px', border: 'none', borderRadius: 8,
              background: '#7c3aed', color: '#fff', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 600, opacity: saving ? 0.6 : 1,
            }}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================
// ACTIVITY DETAIL MODAL
// =============================================
function ActivityDetail({ data, onClose, onEdit, logs, onAddLog }) {
  const [logText, setLogText] = useState('');
  const [sendingLog, setSendingLog] = useState(false);

  if (!data) return null;
  const jenis = jenisInfo[data.jenis] || { label: data.jenis, icon: Tag, color: '#6b7280' };
  const st = statusStyles[data.status] || statusStyles['Pending'];
  const overdue = isOverdue(data.deadline, data.status);

  const handleSendLog = async () => {
    if (!logText.trim()) return;
    setSendingLog(true);
    try {
      await onAddLog(data.id, logText);
      setLogText('');
    } catch (e) {
      console.error(e);
    } finally {
      setSendingLog(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{
                background: jenis.color + '18', color: jenis.color, padding: '3px 10px',
                borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <jenis.icon size={12} /> {jenis.label}
              </span>
              <span style={{
                background: st.bg, color: st.color, padding: '3px 10px',
                borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
              }}>{data.status}</span>
              <span style={{
                background: (priorityColors[data.priority] || '#6b7280') + '18',
                color: priorityColors[data.priority] || '#6b7280',
                padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
              }}>{data.priority}</span>
              {overdue && (
                <span style={{ background: '#fef2f2', color: '#dc2626', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
                  OVERDUE
                </span>
              )}
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{data.judul}</h3>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { onClose(); onEdit(data); }} style={{
              background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem',
            }}><Edit3 size={14} /> Edit</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={20} color="#6b7280" />
            </button>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Progress</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f2937' }}>{data.progress || 0}%</span>
            </div>
            <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 4, transition: 'width 0.3s',
                background: data.progress >= 100 ? '#16a34a' : data.progress >= 50 ? '#2563eb' : '#f59e0b',
                width: `${data.progress || 0}%`,
              }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20 }}>
            {data.nama_prospek && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <User size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Prospek:</span>
                <span style={{ fontWeight: 500 }}>{data.nama_prospek}</span>
              </div>
            )}
            {data.perusahaan && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Building2 size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Perusahaan:</span>
                <span style={{ fontWeight: 500 }}>{data.perusahaan}</span>
              </div>
            )}
            {data.kontak_person && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Phone size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Kontak:</span>
                <span style={{ fontWeight: 500 }}>{data.kontak_person}</span>
              </div>
            )}
            {data.no_hp && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Phone size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>HP:</span>
                <span style={{ fontWeight: 500 }}>{data.no_hp}</span>
              </div>
            )}
            {data.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Mail size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Email:</span>
                <span style={{ fontWeight: 500 }}>{data.email}</span>
              </div>
            )}
            {data.nilai_penawaran > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <DollarSign size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Nilai:</span>
                <span style={{ fontWeight: 600, color: '#16a34a' }}>{formatCurrency(data.nilai_penawaran)}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
              <Calendar size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Tanggal:</span>
              <span>{formatDate(data.tanggal)}</span>
            </div>
            {data.deadline && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Clock size={14} color={overdue ? '#dc2626' : '#6b7280'} />
                <span style={{ color: '#6b7280' }}>Deadline:</span>
                <span style={{ color: overdue ? '#dc2626' : '#1f2937', fontWeight: overdue ? 600 : 400 }}>
                  {formatDate(data.deadline)}
                </span>
              </div>
            )}
            {data.tgl_followup && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Target size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Follow-up:</span>
                <span>{formatDate(data.tgl_followup)}</span>
              </div>
            )}
            {data.assigned_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <User size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Ditugaskan:</span>
                <span style={{ fontWeight: 500 }}>{data.assigned_name}</span>
              </div>
            )}
          </div>

          {data.deskripsi && (
            <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f9fafb', borderRadius: 8, fontSize: '0.85rem', color: '#374151' }}>
              {data.deskripsi}
            </div>
          )}

          {data.catatan && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Catatan:</div>
              <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{data.catatan}</div>
            </div>
          )}

          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <MessageSquare size={14} /> Log Aktivitas ({(logs || []).length})
            </h4>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}
                value={logText}
                onChange={e => setLogText(e.target.value)}
                placeholder="Tambah catatan / follow-up..."
                onKeyDown={e => e.key === 'Enter' && handleSendLog()}
              />
              <button onClick={handleSendLog} disabled={sendingLog || !logText.trim()} style={{
                padding: '8px 16px', border: 'none', borderRadius: 8,
                background: '#7c3aed', color: '#fff', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600, opacity: (sendingLog || !logText.trim()) ? 0.5 : 1,
              }}>{sendingLog ? '...' : 'Kirim'}</button>
            </div>
            {(logs || []).length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center', padding: 12 }}>Belum ada log aktivitas</p>
            ) : (
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {logs.map((log, i) => (
                  <div key={log.id || i} style={{
                    padding: '10px 14px', background: '#f9fafb', borderRadius: 8,
                    marginBottom: 8, fontSize: '0.8rem',
                  }}>
                    <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{log.catatan}</div>
                    <div style={{ color: '#9ca3af', marginTop: 4, fontSize: '0.75rem' }}>
                      {log.user_name || 'System'} • {formatDate(log.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================
// CLIENT FORM MODAL
// =============================================
function ClientForm({ onSave, onClose, initial }) {
  const [form, setForm] = useState(initial || {
    nama_klien: '', perusahaan: '', alamat: '', kontak_person: '', no_hp: '',
    email: '', website: '', kategori: 'Prospek', sumber: '', potensi_penjualan: 0,
    status: 'Active', last_contact: '', next_followup: '', catatan: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama_klien.trim()) { setError('Nama klien harus diisi'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        potensi_penjualan: parseFloat(form.potensi_penjualan) || 0,
        last_contact: form.last_contact || null,
        next_followup: form.next_followup || null,
      };
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0',
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={20} style={{ color: '#0891b2' }} />
            {initial ? 'Edit Klien' : 'Tambah Klien'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#6b7280" />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.85rem' }}>
              <AlertCircle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{error}
            </div>
          )}

          {/* Informasi Dasar */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Nama Klien *</label>
              <input style={inputStyle} value={form.nama_klien} onChange={e => set('nama_klien', e.target.value)}
                placeholder="Nama klien / perusahaan" />
            </div>
            <div>
              <label style={labelStyle}>Perusahaan</label>
              <input style={inputStyle} value={form.perusahaan} onChange={e => set('perusahaan', e.target.value)}
                placeholder="Nama perusahaan" />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Alamat</label>
            <textarea style={{ ...inputStyle, minHeight: 50 }} value={form.alamat}
              onChange={e => set('alamat', e.target.value)} placeholder="Alamat lengkap..." rows={2} />
          </div>

          {/* Kontak */}
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0', paddingTop: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Phone size={14} /> Informasi Kontak
            </h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Kontak Person</label>
              <input style={inputStyle} value={form.kontak_person} onChange={e => set('kontak_person', e.target.value)}
                placeholder="Nama kontak" />
            </div>
            <div>
              <label style={labelStyle}>No HP</label>
              <input style={inputStyle} value={form.no_hp} onChange={e => set('no_hp', e.target.value)}
                placeholder="08xxxxxxxxxx" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="email@domain.com" />
            </div>
            <div>
              <label style={labelStyle}>Website</label>
              <input style={inputStyle} value={form.website} onChange={e => set('website', e.target.value)}
                placeholder="https://..." />
            </div>
          </div>

          {/* Kategori & Sales */}
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0', paddingTop: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Tag size={14} /> Kategori & Sales
            </h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Kategori</label>
              <select style={inputStyle} value={form.kategori} onChange={e => set('kategori', e.target.value)}>
                {KATEGORI_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {CLIENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Sumber</label>
              <select style={inputStyle} value={form.sumber} onChange={e => set('sumber', e.target.value)}>
                <option value="">-</option>
                {SUMBER_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Potensi Penjualan (Rp)</label>
              <input style={inputStyle} type="number" value={form.potensi_penjualan}
                onChange={e => set('potensi_penjualan', e.target.value)} placeholder="0" />
            </div>
          </div>

          {/* Jadwal */}
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0', paddingTop: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={14} /> Jadwal Follow-up
            </h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Kontak Terakhir</label>
              <input style={inputStyle} type="date" value={form.last_contact || ''}
                onChange={e => set('last_contact', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Follow-up Berikutnya</label>
              <input style={inputStyle} type="date" value={form.next_followup || ''}
                onChange={e => set('next_followup', e.target.value)} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Catatan</label>
            <textarea style={{ ...inputStyle, minHeight: 60 }} value={form.catatan}
              onChange={e => set('catatan', e.target.value)} placeholder="Catatan tentang klien..." rows={2} />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8,
              background: '#fff', cursor: 'pointer', fontSize: '0.875rem',
            }}>Batal</button>
            <button type="submit" disabled={saving} style={{
              padding: '10px 24px', border: 'none', borderRadius: 8,
              background: '#0891b2', color: '#fff', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 600, opacity: saving ? 0.6 : 1,
            }}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================
// CLIENT DETAIL MODAL
// =============================================
function ClientDetail({ data, onClose, onEdit }) {
  if (!data) return null;
  const kat = kategoriColors[data.kategori] || '#6b7280';
  const st = clientStatusStyles[data.status] || clientStatusStyles['Active'];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{
                background: kat + '18', color: kat, padding: '3px 10px',
                borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
              }}>{data.kategori}</span>
              <span style={{
                background: st.bg, color: st.color, padding: '3px 10px',
                borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
              }}>{data.status}</span>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{data.nama_klien}</h3>
            {data.perusahaan && <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>{data.perusahaan}</p>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { onClose(); onEdit(data); }} style={{
              background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem',
            }}><Edit3 size={14} /> Edit</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={20} color="#6b7280" />
            </button>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>
            {data.kontak_person && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <User size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Kontak:</span>
                <span style={{ fontWeight: 500 }}>{data.kontak_person}</span>
              </div>
            )}
            {data.no_hp && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Phone size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>HP:</span>
                <span style={{ fontWeight: 500 }}>{data.no_hp}</span>
              </div>
            )}
            {data.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Mail size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Email:</span>
                <span style={{ fontWeight: 500 }}>{data.email}</span>
              </div>
            )}
            {data.website && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Globe size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Website:</span>
                <span style={{ fontWeight: 500 }}>{data.website}</span>
              </div>
            )}
            {data.sumber && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Tag size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Sumber:</span>
                <span style={{ fontWeight: 500 }}>{data.sumber}</span>
              </div>
            )}
            {data.potensi_penjualan > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <DollarSign size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Potensi:</span>
                <span style={{ fontWeight: 600, color: '#16a34a' }}>{formatCurrency(data.potensi_penjualan)}</span>
              </div>
            )}
            {data.last_contact && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Clock size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Kontak Terakhir:</span>
                <span>{formatDate(data.last_contact)}</span>
              </div>
            )}
            {data.next_followup && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                <Target size={14} color="#6b7280" /><span style={{ color: '#6b7280' }}>Follow-up:</span>
                <span style={{ fontWeight: 500 }}>{formatDate(data.next_followup)}</span>
              </div>
            )}
          </div>

          {data.alamat && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={12} /> Alamat
              </div>
              <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{data.alamat}</div>
            </div>
          )}

          {data.catatan && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Catatan:</div>
              <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{data.catatan}</div>
            </div>
          )}

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb', fontSize: '0.75rem', color: '#9ca3af' }}>
            Dibuat: {formatDate(data.created_at)} {data.creator_name ? `oleh ${data.creator_name}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================
// CLIENT LIST TAB
// =============================================
function ClientTab() {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterKategori, setFilterKategori] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => {
    setLoading(true);
    getMarketingClients()
      .then(res => { setData(res.data || []); setFiltered(res.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
    getMarketingClientStats()
      .then(res => setStats(res.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let result = [...data];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.nama_klien?.toLowerCase().includes(q) ||
        d.perusahaan?.toLowerCase().includes(q) ||
        d.kontak_person?.toLowerCase().includes(q) ||
        d.no_hp?.toLowerCase().includes(q) ||
        d.email?.toLowerCase().includes(q)
      );
    }
    if (filterKategori) result = result.filter(d => d.kategori === filterKategori);
    if (filterStatus) result = result.filter(d => d.status === filterStatus);
    setFiltered(result);
  }, [search, filterKategori, filterStatus, data]);

  const handleCreate = async (payload) => {
    await createMarketingClient(payload);
    setShowForm(false);
    load();
  };

  const handleUpdate = async (payload) => {
    await updateMarketingClient(editData.id, payload);
    setEditData(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus klien ini?')) return;
    try {
      await deleteMarketingClient(id);
      setData(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal hapus');
    }
  };

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          <StatCard icon={Users} label="Total Klien" value={stats.total || 0} color="#0891b2" />
          <StatCard icon={CheckCircle} label="Klien Aktif" value={stats.active || 0} color="#16a34a" />
          <StatCard icon={AlertCircle} label="Inactive" value={stats.inactive || 0} color="#6b7280" />
          <StatCard icon={Clock} label="On Hold" value={stats.on_hold || 0} color="#f59e0b" />
          <StatCard icon={Target} label="Follow-up Minggu Ini" value={stats.followup_week || 0} color="#7c3aed" />
          <StatCard icon={DollarSign} label="Total Potensi" value={formatCurrency(stats.total_potensi)} color="#16a34a" />
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center',
        padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb',
      }}>
        <div style={{ position: 'relative', flex: '1 1 240px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{
              width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #d1d5db',
              borderRadius: 8, fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box',
            }}
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cari nama klien, perusahaan, kontak..."
          />
        </div>
        <select value={filterKategori} onChange={e => setFilterKategori(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}>
          <option value="">Semua Kategori</option>
          {KATEGORI_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}>
          <option value="">Semua Status</option>
          {CLIENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Client List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Memuat data...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <Users size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>Belum ada data klien</p>
          <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ marginTop: 8 }}>
            <Plus size={16} /> Tambah Klien Pertama
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filtered.map(item => {
            const kat = kategoriColors[item.kategori] || '#6b7280';
            const st = clientStatusStyles[item.status] || clientStatusStyles['Active'];
            return (
              <div key={item.id} style={{
                background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
                padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16,
                flexWrap: 'wrap', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                borderLeft: `4px solid ${kat}`,
              }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>{item.nama_klien}</span>
                    {item.perusahaan && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>• {item.perusahaan}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: '#6b7280', flexWrap: 'wrap' }}>
                    {item.kontak_person && <span><User size={11} style={{ verticalAlign: -1 }} /> {item.kontak_person}</span>}
                    {item.no_hp && <span><Phone size={11} style={{ verticalAlign: -1 }} /> {item.no_hp}</span>}
                    {item.email && <span><Mail size={11} style={{ verticalAlign: -1 }} /> {item.email}</span>}
                    {item.potensi_penjualan > 0 && <span><DollarSign size={11} style={{ verticalAlign: -1 }} /> {formatCurrency(item.potensi_penjualan)}</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{
                    background: kat + '18', color: kat, padding: '3px 10px',
                    borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                  }}>{item.kategori}</span>
                  <span style={{
                    background: st.bg, color: st.color, padding: '3px 10px',
                    borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                  }}>{item.status}</span>
                  {item.next_followup && (
                    <span style={{
                      background: '#f0fdf4', color: '#16a34a', padding: '3px 10px',
                      borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                    }}>Follow-up: {formatDate(item.next_followup)}</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setViewDetail(item)} style={{
                    background: '#f3f4f6', border: 'none', borderRadius: 6, padding: 6,
                    cursor: 'pointer', display: 'flex',
                  }} title="Lihat Detail"><Eye size={16} color="#6b7280" /></button>
                  <button onClick={() => { setEditData(item); setShowForm(true); }} style={{
                    background: '#f3f4f6', border: 'none', borderRadius: 6, padding: 6,
                    cursor: 'pointer', display: 'flex',
                  }} title="Edit"><Edit3 size={16} color="#6b7280" /></button>
                  <button onClick={() => handleDelete(item.id)} style={{
                    background: '#fef2f2', border: 'none', borderRadius: 6, padding: 6,
                    cursor: 'pointer', display: 'flex',
                  }} title="Hapus"><Trash2 size={16} color="#dc2626" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ClientForm
          initial={editData}
          onSave={editData ? handleUpdate : handleCreate}
          onClose={() => { setShowForm(false); setEditData(null); }}
        />
      )}

      {/* Detail Modal */}
      {viewDetail && (
        <ClientDetail
          data={viewDetail}
          onClose={() => setViewDetail(null)}
          onEdit={(item) => { setViewDetail(null); setEditData(item); setShowForm(true); }}
        />
      )}
    </div>
  );
}

// =============================================
// MAIN COMPONENT (with Tabs)
// =============================================
export default function MarketingActivity() {
  const [activeTab, setActiveTab] = useState('activity');
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterJenis, setFilterJenis] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  const load = () => {
    setLoading(true);
    getMarketingActivities()
      .then(res => {
        setData(res.data || []);
        setFiltered(res.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    getMarketingStats()
      .then(res => setStats(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    getUsers().then(res => setUsers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let result = [...data];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.judul?.toLowerCase().includes(q) ||
        d.nama_prospek?.toLowerCase().includes(q) ||
        d.perusahaan?.toLowerCase().includes(q) ||
        d.kontak_person?.toLowerCase().includes(q) ||
        d.deskripsi?.toLowerCase().includes(q)
      );
    }
    if (filterJenis) result = result.filter(d => d.jenis === filterJenis);
    if (filterStatus) result = result.filter(d => d.status === filterStatus);
    if (filterPriority) result = result.filter(d => d.priority === filterPriority);
    setFiltered(result);
  }, [search, filterJenis, filterStatus, filterPriority, data]);

  const handleCreate = async (payload) => {
    await createMarketingActivity(payload);
    setShowForm(false);
    load();
  };

  const handleUpdate = async (payload) => {
    await updateMarketingActivity(editData.id, payload);
    setEditData(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus marketing activity ini?')) return;
    try {
      await deleteMarketingActivity(id);
      setData(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal hapus');
    }
  };

  const openDetail = async (item) => {
    setViewDetail(item);
    try {
      const res = await getMarketingLogs(item.id);
      setDetailLogs(res.data || []);
    } catch { setDetailLogs([]); }
  };

  const handleAddLog = async (activityId, catatan) => {
    await createMarketingLog({ marketing_activity_id: activityId, catatan });
    const res = await getMarketingLogs(activityId);
    setDetailLogs(res.data || []);
  };

  const grouped = {};
  filtered.forEach(item => {
    const j = item.jenis || 'other';
    if (!grouped[j]) grouped[j] = [];
    grouped[j].push(item);
  });

  const tabStyle = (active) => ({
    padding: '10px 20px', border: 'none', borderRadius: 8,
    background: active ? '#7c3aed' : 'transparent',
    color: active ? '#fff' : '#6b7280',
    cursor: 'pointer', fontSize: '0.875rem', fontWeight: active ? 600 : 500,
    display: 'flex', alignItems: 'center', gap: 6,
    transition: 'all 0.2s',
  });

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Megaphone size={24} style={{ color: '#7c3aed' }} />
              Marketing & Sales
            </h2>
            <p className="page-desc">
              Kelola aktivitas marketing, client relations, admin support & database klien CRM
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => {
            if (activeTab === 'activity') { setEditData(null); setShowForm(true); }
            else { setEditData(null); setShowForm(true); }
          }}>
            <Plus size={16} /> {activeTab === 'activity' ? 'Tambah Aktivitas' : 'Tambah Klien'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 24,
        padding: '6px', background: '#f3f4f6', borderRadius: 12, width: 'fit-content',
      }}>
        <button onClick={() => setActiveTab('activity')} style={tabStyle(activeTab === 'activity')}>
          <Megaphone size={16} /> Aktivitas
        </button>
        <button onClick={() => setActiveTab('klien')} style={tabStyle(activeTab === 'klien')}>
          <Users size={16} /> Klien
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'activity' ? (
        <>
          {/* Activity Stats */}
          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              <StatCard icon={Megaphone} label="Total Aktivitas" value={stats.total || 0} color="#7c3aed" />
              <StatCard icon={CheckCircle} label="Selesai" value={stats.completed || 0} color="#16a34a" />
              <StatCard icon={Clock} label="In Progress" value={stats.in_progress || 0} color="#2563eb" />
              <StatCard icon={AlertCircle} label="Pending" value={stats.pending || 0} color="#f59e0b" />
              <StatCard icon={Flag} label="Overdue" value={stats.overdue || 0} color="#dc2626" sub={stats.overdue > 0 ? '⚠ Perlu perhatian' : ''} />
              <StatCard icon={Target} label="Follow-up Hari Ini" value={stats.followup_today || 0} color="#0891b2" />
              <StatCard icon={DollarSign} label="Total Nilai Penawaran" value={formatCurrency(stats.total_nilai)} color="#16a34a" />
            </div>
          )}

          {/* Filters */}
          <div style={{
            display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center',
            padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb',
          }}>
            <div style={{ position: 'relative', flex: '1 1 240px' }}>
              <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                style={{
                  width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box',
                }}
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Cari judul, prospek, perusahaan..."
              />
            </div>
            <select value={filterJenis} onChange={e => setFilterJenis(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}>
              <option value="">Semua Jenis</option>
              {JENIS_OPTIONS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}>
              <option value="">Semua Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}>
              <option value="">Semua Prioritas</option>
              {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Activity List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Memuat data...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
              <Megaphone size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>Belum ada aktivitas marketing</p>
              <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ marginTop: 8 }}>
                <Plus size={16} /> Tambah Aktivitas Pertama
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {JENIS_OPTIONS.filter(j => grouped[j.value]?.length > 0).map(jenis => (
                <div key={jenis.value}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, marginTop: 4,
                  }}>
                    <jenis.icon size={16} style={{ color: jenis.color }} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: jenis.color }}>{jenis.label}</span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>({grouped[jenis.value].length})</span>
                  </div>
                  {grouped[jenis.value].map(item => {
                    const st = statusStyles[item.status] || statusStyles['Pending'];
                    const overdue = isOverdue(item.deadline, item.status);
                    return (
                      <div key={item.id} style={{
                        background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
                        padding: '14px 18px', marginBottom: 8,
                        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        borderLeft: `4px solid ${overdue ? '#dc2626' : jenis.color}`,
                      }}>
                        <div style={{ minWidth: 40, textAlign: 'center' }}>
                          <div style={{
                            width: 40, height: 40, borderRadius: '50%',
                            border: `3px solid ${item.progress >= 100 ? '#16a34a' : '#e5e7eb'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 700,
                            color: item.progress >= 100 ? '#16a34a' : '#6b7280',
                          }}>
                            {item.progress || 0}%
                          </div>
                        </div>

                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>{item.judul}</span>
                            {overdue && <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>⚠ OVERDUE</span>}
                          </div>
                          <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: '#6b7280', flexWrap: 'wrap' }}>
                            {item.nama_prospek && <span><User size={11} style={{ verticalAlign: -1 }} /> {item.nama_prospek}</span>}
                            {item.perusahaan && <span><Building2 size={11} style={{ verticalAlign: -1 }} /> {item.perusahaan}</span>}
                            {item.deadline && <span><Calendar size={11} style={{ verticalAlign: -1 }} /> Deadline: {formatDate(item.deadline)}</span>}
                            {item.nilai_penawaran > 0 && <span><DollarSign size={11} style={{ verticalAlign: -1 }} /> {formatCurrency(item.nilai_penawaran)}</span>}
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{
                            background: st.bg, color: st.color, padding: '3px 10px',
                            borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          }}>{item.status}</span>
                          <span style={{
                            background: (priorityColors[item.priority] || '#6b7280') + '18',
                            color: priorityColors[item.priority] || '#6b7280',
                            padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          }}>{item.priority}</span>
                        </div>

                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => openDetail(item)} style={{
                            background: '#f3f4f6', border: 'none', borderRadius: 6, padding: 6,
                            cursor: 'pointer', display: 'flex',
                          }} title="Lihat Detail"><Eye size={16} color="#6b7280" /></button>
                          <button onClick={() => { setEditData(item); setShowForm(true); }} style={{
                            background: '#f3f4f6', border: 'none', borderRadius: 6, padding: 6,
                            cursor: 'pointer', display: 'flex',
                          }} title="Edit"><Edit3 size={16} color="#6b7280" /></button>
                          <button onClick={() => handleDelete(item.id)} style={{
                            background: '#fef2f2', border: 'none', borderRadius: 6, padding: 6,
                            cursor: 'pointer', display: 'flex',
                          }} title="Hapus"><Trash2 size={16} color="#dc2626" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <ActivityForm
              initial={editData}
              onSave={editData ? handleUpdate : handleCreate}
              onClose={() => { setShowForm(false); setEditData(null); }}
              users={users}
            />
          )}

          {/* Detail Modal */}
          {viewDetail && (
            <ActivityDetail
              data={viewDetail}
              onClose={() => { setViewDetail(null); setDetailLogs([]); }}
              onEdit={(item) => { setViewDetail(null); setEditData(item); setShowForm(true); }}
              logs={detailLogs}
              onAddLog={handleAddLog}
            />
          )}
        </>
      ) : (
        <ClientTab />
      )}
    </div>
  );
}
